import styled from '@emotion/styled';
import LoggingClick from '../../../utils/logger/components/LoggingClick';
import { usePageLogger } from '../../../utils/logger/hooks/usePageLogger';
import type { ReviewImage } from '..';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2e2e2e;
  margin: 0;
`;

const ViewAllButton = styled.button`
  background: none !important;
  border: none !important;
  color: #737373 !important;
  font-size: 12px !important;
  cursor: pointer !important;
  padding: 0 !important;
  font-family: 'Plus Jakarta Sans', sans-serif !important;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  background: #f8f8f8;
  border-radius: 8px;
  margin-top: 8px;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #6e6e6e;
  text-align: center;
  margin: 0;
`;

const ImageGrid = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ImageContainer = styled.div`
  flex: 0 0 100px;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

interface ReviewImageGalleryProps {
  images: ReviewImage[];
  onViewAll?: () => void;
  productId?: string | number;
}

export const ReviewImageGallery = ({ images, onViewAll, productId }: ReviewImageGalleryProps) => {
  const logger = usePageLogger();

  if (!images || images.length === 0) {
    return (
      <Container>
        <Header>
          <Title>Customer Images</Title>
        </Header>
        <EmptyStateContainer>
          <EmptyStateText>No customer images yet</EmptyStateText>
        </EmptyStateContainer>
      </Container>
    );
  }

  const handleImageClick = () => {
    // Log individual image click
    logger?.event('customer_image_individual_click', 'click', {
      productId: productId?.toString(),
      section: 'customer_images',
      total_images: images.length,
    });

    // Individual image clicks now just trigger the view all modal
    if (onViewAll) {
      onViewAll();
    }
  };

  return (
    <Container>
      <Header>
        <Title>Customer Images</Title>
        <LoggingClick
          logName="customer_images_view_all_button"
          params={{ productId: productId?.toString(), section: 'customer_images', images_count: images.length }}
        >
          <ViewAllButton onClick={onViewAll}>View all</ViewAllButton>
        </LoggingClick>
      </Header>
      <ImageGrid>
        {images.map((image, index) => (
          <ImageContainer key={image.id} onClick={handleImageClick}>
            <Image src={image.url} alt={image.alt} />
          </ImageContainer>
        ))}
      </ImageGrid>
    </Container>
  );
};

export default ReviewImageGallery;
